/**
 * EditorRegistryContext - 编辑器实例注册表
 *
 * 核心目的：维护 Map<editorId, EditorInstance>，每个 TipTapEditor 实例
 * 通过此 Context 注册自己的身份（noteId、mode、状态等）。
 *
 * 解决的问题（Phase 4 长期重构）：
 * - 当前代码中"当前操作笔记"的来源不明确：可能是 props noteId、Redux state.notes.currentNote
 * - 在多编辑器场景下，currentNote 是"最后焦点笔记"，可能与"当前操作笔记"不一致
 * - EditorRegistry 提供一个权威的"所有活跃编辑器实例列表"，让"当前操作笔记"的查找不依赖全局状态
 *
 * 用法：
 *   // 在 App 根组件
 *   <EditorRegistryProvider>
 *     <App />
 *   </EditorRegistryProvider>
 *
 *   // 在 TipTapEditor 内部
 *   const { registerEditor, unregisterEditor } = useEditorRegistry()
 *   const editorId = useEditorIdentity(noteId).editorId
 *
 *   useEffect(() => {
 *     registerEditor({ editorId, noteId, mode, isActive: true, lastActivityAt: Date.now() })
 *     return () => unregisterEditor(editorId)
 *   }, [editorId, noteId, mode])
 *
 *   // 查找特定 noteId 对应的所有编辑器实例
 *   const editors = useEditorInstancesByNoteId(noteId)
 *
 * 设计原则（与 Logseq/CRDT 思想对齐）：
 * 1. 注册表是"活跃编辑器"的真相源（替代全局 currentNote）
 * 2. 编辑器实例在自己的生命周期内管理自己（注册/注销）
 * 3. 不影响现有 Redux 架构（向后兼容）
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  ReactNode
} from 'react'

/** 编辑器实例身份 */
export interface EditorInstance {
  /** 编辑器唯一 ID（来自 useEditorIdentity Hook） */
  editorId: string
  /** 编辑器实例的 noteId */
  noteId: string
  /** 编辑器模式 */
  mode: 'standard' | 'crdt' | 'suji' | 'right' | 'left'
  /** 是否为激活状态（用户最后操作的编辑器） */
  isActive: boolean
  /** 最后活动时间戳（用于 LRU 淘汰等场景） */
  lastActivityAt: number
  /**
   * 外部内容变更回调（跨编辑器直接通知通道）
   *
   * 当其他编辑器通过"甩到笔记"等功能修改了本编辑器所打开笔记的内容时，
   * 通过此回调直接通知本编辑器刷新，绕过 Electric Sync 和 useSyncCollectionToRedux 的延迟。
   * 回调接收完整的笔记 content（JSON 字符串），由编辑器自行 diff 应用。
   */
  onExternalContentChange?: (content: string) => void
}

interface EditorRegistryContextValue {
  /** 注册一个编辑器实例 */
  registerEditor: (instance: EditorInstance) => void
  /** 注销一个编辑器实例 */
  unregisterEditor: (editorId: string) => void
  /** 更新编辑器实例的部分字段 */
  updateEditor: (editorId: string, updates: Partial<EditorInstance>) => void
  /** 获取所有活跃编辑器实例 */
  getAllEditors: () => EditorInstance[]
  /** 根据 noteId 获取对应的所有编辑器实例 */
  getEditorsByNoteId: (noteId: string) => EditorInstance[]
  /** 获取当前激活的编辑器实例 */
  getActiveEditor: () => EditorInstance | null
  /** 设置激活的编辑器 */
  setActiveEditor: (editorId: string) => void
  /** 当前激活的编辑器（响应式） */
  activeEditor: EditorInstance | null
  /**
   * 通知指定 noteId 的所有编辑器实例：外部内容已变更
   *
   * 用于"甩到笔记"跨编辑器场景：源编辑器调用此方法，目标编辑器（若已打开）
   * 会立即收到新内容并刷新，绕过 Electric Sync 的延迟。
   *
   * sourceEditorId 用于多编辑器同笔记场景：保存方调用时传入自己的 editorId，
   * 注册表会跳过发送者本身，避免 onExternalContentChange → setContent → onUpdate → debouncedSave 回环。
   * 不传时通知所有打开该 noteId 的编辑器（保持甩到笔记场景的原有行为）。
   */
  notifyNoteContentChange: (noteId: string, content: string, sourceEditorId?: string) => void
}

const EditorRegistryContext = createContext<EditorRegistryContextValue | null>(null)

// 导出 context 供 useContext 直接使用（可选，用于无法使用 useEditorRegistry 的场景）
export { EditorRegistryContext }

/**
 * EditorRegistryProvider - 注册表 Provider
 */
export const EditorRegistryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // 使用 Map 维护实例，使用 ref 避免触发不必要的 re-render
  const instancesRef = useRef<Map<string, EditorInstance>>(new Map())

  // 当前激活的 editorId（响应式）
  const [activeEditorId, setActiveEditorId] = useState<string | null>(null)

  const registerEditor = useCallback((instance: EditorInstance) => {
    instancesRef.current.set(instance.editorId, instance)
  }, [])

  const unregisterEditor = useCallback((editorId: string) => {
    instancesRef.current.delete(editorId)
    if (activeEditorId === editorId) {
      setActiveEditorId(null)
    }
  }, [activeEditorId])

  const updateEditor = useCallback((editorId: string, updates: Partial<EditorInstance>) => {
    const existing = instancesRef.current.get(editorId)
    if (existing) {
      instancesRef.current.set(editorId, { ...existing, ...updates })
    }
  }, [])

  const getAllEditors = useCallback(() => {
    return Array.from(instancesRef.current.values())
  }, [])

  const getEditorsByNoteId = useCallback((noteId: string) => {
    return Array.from(instancesRef.current.values()).filter(e => e.noteId === noteId)
  }, [])

  const getActiveEditor = useCallback(() => {
    if (!activeEditorId) return null
    return instancesRef.current.get(activeEditorId) || null
  }, [activeEditorId])

  const setActiveEditor = useCallback((editorId: string) => {
    setActiveEditorId(editorId)
    // 同时更新实例的 isActive
    const existing = instancesRef.current.get(editorId)
    if (existing) {
      instancesRef.current.set(editorId, { ...existing, isActive: true, lastActivityAt: Date.now() })
    }
  }, [])

  const notifyNoteContentChange = useCallback((noteId: string, content: string, sourceEditorId?: string) => {
    instancesRef.current.forEach((editor) => {
      // 排除发送者自己，避免 onExternalContentChange → setContent → onUpdate → debouncedSave 回环
      if (sourceEditorId && editor.editorId === sourceEditorId) return
      if (editor.noteId === noteId && editor.onExternalContentChange) {
        try {
          editor.onExternalContentChange(content)
        } catch (err) {
          console.warn('⚠️ [EditorRegistry] onExternalContentChange 回调执行失败:', err)
        }
      }
    })
  }, [])

  const activeEditor = useMemo(() => {
    if (!activeEditorId) return null
    return instancesRef.current.get(activeEditorId) || null
  }, [activeEditorId])

  const value = useMemo<EditorRegistryContextValue>(() => ({
    registerEditor,
    unregisterEditor,
    updateEditor,
    getAllEditors,
    getEditorsByNoteId,
    getActiveEditor,
    setActiveEditor,
    activeEditor,
    notifyNoteContentChange
  }), [registerEditor, unregisterEditor, updateEditor, getAllEditors, getEditorsByNoteId, getActiveEditor, setActiveEditor, activeEditor, notifyNoteContentChange])

  return (
    <EditorRegistryContext.Provider value={value}>
      {children}
    </EditorRegistryContext.Provider>
  )
}

/**
 * useEditorRegistry - Hook 访问编辑器注册表
 */
export function useEditorRegistry(): EditorRegistryContextValue {
  const ctx = useContext(EditorRegistryContext)
  if (!ctx) {
    throw new Error('useEditorRegistry must be used within EditorRegistryProvider')
  }
  return ctx
}

/**
 * useEditorInstancesByNoteId - 获取特定 noteId 的所有编辑器实例
 */
export function useEditorInstancesByNoteId(noteId: string): EditorInstance[] {
  const { getEditorsByNoteId } = useEditorRegistry()
  return useMemo(() => getEditorsByNoteId(noteId), [getEditorsByNoteId, noteId])
}
